import React from 'react'
import { Box, Typography } from '@mui/material'
import { ExternalUrlsAccumulatorJob } from './types'
import { ExternalUrlsNodesTable } from './ExternalUrlsNodesTable'
import { ExternalUrlsLeavesTable } from './ExternalUrlsLeavesTable'

/**
 * Renders the results of an external URLs accumulator job.
 * Displays host counts summary and separate tables for node and leaf URLs.
 * @param result - The external URLs accumulator job result
 * @returns Formatted display of external URL findings with host statistics
 */
export function ExternalUrlsAccumulatorResponse({
  result,
}: {
  result: ExternalUrlsAccumulatorJob
}) {
  const { data, error } = result

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : JSON.stringify(error)

    return (
      <Box padding="10px">
        <Box>
          <strong>ERROR:</strong>
        </Box>
        <Box padding="5px">{errorMessage}</Box>
      </Box>
    )
  }

  const { nodes, leaves, host_counts } = data.data

  return (
    <Box>
      <HostCountsSummary hostCounts={host_counts} />
      <ExternalUrlsNodesTable externalUrls={nodes} />
      <ExternalUrlsLeavesTable externalUrls={leaves} />
    </Box>
  )
}

function HostCountsSummary({
  hostCounts,
}: {
  hostCounts: Record<string, number>
}) {
  const entries = Object.entries(hostCounts).sort((a, b) => b[1] - a[1])

  if (entries.length === 0) {
    return (
      <Box padding="10px">
        <Typography variant="h6">No URLs found</Typography>
      </Box>
    )
  }

  return (
    <Box padding="10px">
      <Typography variant="h6">Host Counts Summary</Typography>
      <Box padding="5px">
        <Typography variant="body2">Total hosts: {entries.length}</Typography>
        <Typography variant="body2">
          Total URLs: {entries.reduce((sum, [, count]) => sum + count, 0)}
        </Typography>
      </Box>
      <Box padding="5px">
        {entries.map(([host, count]) => (
          <Box key={host} style={{ marginBottom: '4px' }}>
            <Typography variant="body2">
              <strong>{host}</strong>: {count} URL{count !== 1 ? 's' : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
